<script setup lang="ts">
import { PlayerAccountClient } from '@core/types/clients'

const route = useRoute()
const { env } = useCoreConfig()
const { consentCode } = useRecurringConsent()
const { isLoggedIn, data: user, userJustLoggedIn, userJustSignedUp } = useUserInfo()
const { required } = useRules()
const { isActive: isTermsDialogActive } = useTermsDialog()
const { init: initMobilePasskeys } = useMobileAppPasskeys()
const { isModalOpen: isBonusPlayModalOpen } = useBonusPlayCheck()
// const { showAppPrompt } = useAppPrompt() // Disabled until we find a smarter solution concerning when to prompt the user

const isUserJustLoggedIn = userJustLoggedIn.value

const isPromotionTermsActive = computed(() => isTermsDialogActive.value || route.query['excluded-games'])

// PaymentsReceipt
const { receiptTransaction } = useDepositReceipt({ followRoute: true })

onMounted(() => {
    initMobilePasskeys()
})

const allowMoneyLimitsDialog = computed(() => {
    if (route.name === 'users-money-limits') return false

    return true
})

// UIPassKeyModal
const { isMobileApp, isMobileAppAndRequiresUpdate } = useMobileApp()
// Passkey auto-login is owned by the core react-handles plugin (single watcher); nothing to wire here.

const showRecurringConsent = computed(() => {
    if (!user.value?.phone?.length) return false
    if (route.name === 'users-marketing') return false

    return isUserJustLoggedIn || !!userJustSignedUp.value || !!consentCode.value
})

// Latched: saving clears consentCode, which would otherwise unmount the step mid-save.
const recurringConsentMounted = ref(false)
whenever(showRecurringConsent, () => {
    recurringConsentMounted.value = true
}, { immediate: true })

// Latched: the auth sheet animates out after ?auth has left the URL, so the container stays until it reports closed.
const isAuthMounted = ref(false)
whenever(() => !!route.query.auth, () => {
    isAuthMounted.value = true
}, { immediate: true })

// Latched like auth: the terms animate out after their link has left the URL. They report closed before
// router.back() has landed, so the latch drops on closed alone; the next link latches it again.
const isTermsMounted = ref(false)
whenever(() => !!isPromotionTermsActive.value, () => {
    isTermsMounted.value = true
}, { immediate: true })
</script>

<template>
    <div class="overlay-container">
        <template v-if="isLoggedIn">
            <!-- Dialogs that show notifications -->
            <NimbusNotificationDialogContainer />
            <!-- ./ Dialogs that show notifications -->

            <!-- Recurring consent (shown after signup/login, even on deposit page) -->
            <LazyConsentRecurringContainer
                v-if="recurringConsentMounted"
                :checkbox-rules="[required()]"
                prefix-to-remove="0"
            />
            <LazyPaymentsDepositReceipt
                v-if="receiptTransaction"
                :key="receiptTransaction.txId"
                :tx-id="receiptTransaction.txId"
                :psp="receiptTransaction.psp"
                z-index="21"
            />
            <ConsentChangedContainer />
            <LazyUserBlockExpiredDialog v-if="user?.checkResult?.type === PlayerAccountClient.UserCheckResultType.expired_block_must_be_removed_by_customer_service" />
            <LazyMissingPhoneDialog v-if="!user?.phone?.length" />

            <!-- App install dialog -->
            <!-- Disabled until we find a smarter solution concerning when to prompt the user -->
            <!-- <ClientOnly>
                <LazyNimbusMobileAppInstallPrompt
                    v-if="!isMobileApp && showAppPrompt"
                    title="Download our new app"
                    description="New app, new adventures! Download the Happy Tiger Bingo & Slots app today! Click the button ⤵️"
                    maybe-later="Maybe later"
                    :logo="{ src: '/media/512x512.png', alt: 'Happy Tiger Bingo & Slots logo' }"
                    :android="{ ...app.android, alt: 'Download Happy Tiger Bingo & Slots in Google Play Store' }"
                    :ios="{ ...app.ios, alt: 'Download Happy Tiger Bingo & Slots in App Store' }"
                />
            </ClientOnly> -->

            <LazyPaymentsMoneyLimitsDialog v-if="allowMoneyLimitsDialog" />

            <LazyBonusPlayModal v-if="isBonusPlayModalOpen" />
        </template>

        <template v-else>
            <LazyAuthContainer v-if="isAuthMounted" @closed="isAuthMounted = !!route.query.auth" />
            <LazyPromotionExpired v-if="route.query.code" />
        </template>

        <!-- Mobile App Version dialog -->
        <ClientOnly v-if="isMobileAppAndRequiresUpdate">
            <LazyNimbusMobileAppVersionDialog />
        </ClientOnly>

        <LazyPromotionTermsContainer v-if="isTermsMounted" @closed="isTermsMounted = false" />
        <LazyEnvBanner v-if="env !== 'prod' && !route.meta.hideEnvBanner" />
    </div>
</template>
